/**
 * PagesPills — pills row (in the chrome bar's right cluster) showing every
 * page in the active project.
 *
 *   [ 1 · Main ] [ 2 ] [ 3 · Drops ] [ 4 ] [ ⋯ ]
 *
 * Each page is a rounded square that fills the bar's height and grows to
 * share whatever width the right flank has free (operator 2026-10-05:
 * "utilise the space in the top bar for pages, make them rounded cornered
 * squares and maximise clicking space"). Active is filled, inactive outlined.
 * `<` / `>` (prev/next, no wrap, disabled at ends) only appear once the pages
 * no longer fit and the strip has to scroll - while every page is on screen
 * they would just take width away from the pages themselves. `⋯` opens the
 * page manager modal where the user can rename, duplicate, delete,
 * drag-reorder, and cycle hue per page.
 *
 * Hidden entirely when the project has only one page — the only-page
 * case has no use for nav. A one-page project grows through Navigate's
 * Pages card (the manager, whose "+ Add page" works at any count) or the
 * edit-mode canvas menu's "+ Page". The manager itself is mounted by App
 * (layout/pageManagerStore.ts) so it exists even while these pills do not.
 */

import { createEffect, createMemo, createSignal, on, onCleanup, onMount, For, Show } from 'solid-js';
import {
  activeProject,
  pagesOfActive,
  renamePage,
  stepPage,
  switchPage,
  type PageHue,
} from '../layout/projectStore.ts';
import { isEditMode } from '../layout/store.ts';
import { openPageManager } from '../layout/pageManagerStore.ts';
import { pageGlowing } from '../timeline/canvasLights.ts';

const MAX_PILL_NAME_CHARS = 14;

// Page peek (SD-8): hold a pill this long to switch to its page ONLY while
// held (lighting-console momentary idiom), snapping back on release. Above
// the tap arbiter, below long-press territory. A quick tap switches normally.
const PAGE_PEEK_HOLD_MS = 350;
// Movement past this (px) before the hold lands means it was a scroll/drag of
// the pills row, not a press-to-peek — cancel so the list can still scroll.
const PAGE_PEEK_MOVE_TOL = 10;

export function PagesPills() {
  const pages = pagesOfActive;
  /** Double-click a page's tab to rename it there (2026-10-16: a page was
   *  renamed only in the Pages manager). A box, not the button, holds the
   *  field: an input inside a button is invalid and Space would press it. */
  const [renamingId, setRenamingId] = createSignal<string | null>(null);
  const activePageId = createMemo(() => activeProject()?.activePageId ?? null);
  const activeIndex = createMemo(() => {
    const id = activePageId();
    if (!id) return -1;
    return pages().findIndex((p) => p.id === id);
  });

  /** The arrows' next / previous page: the store's `stepPage`, which the canvas swipe uses too. */
  function go(delta: number): void { stepPage(delta); }

  // Overflow: the arrows only show when the pages do not all fit. The pages
  // grow to fill the strip, so while they fit the list's scrollWidth equals
  // its clientWidth; it only exceeds it once every page is at its minimum and
  // there is still not room. Hysteresis: with the arrows showing, the list is
  // narrower by their width, so it must overflow even with that width given
  // back before the arrows stay - otherwise showing them could be the only
  // reason it overflows, and hiding them would flip it back, every frame.
  let listEl: HTMLDivElement | undefined;
  let prevEl: HTMLButtonElement | undefined;
  let nextEl: HTMLButtonElement | undefined;
  const [overflowing, setOverflowing] = createSignal(false);
  function measureOverflow(): void {
    if (!listEl) return;
    const arrowsW = overflowing()
      ? (prevEl?.offsetWidth ?? 0) + (nextEl?.offsetWidth ?? 0) + 8
      : 0;
    setOverflowing(listEl.scrollWidth > listEl.clientWidth + arrowsW + 1);
  }
  // Keep the active page in view when the strip scrolls. Manual scrollLeft
  // rather than scrollIntoView, which would also scroll the scaled .shell.
  function revealActive(): void {
    if (!listEl) return;
    const el = listEl.querySelector<HTMLElement>('.pages-pill[data-active="true"]');
    if (!el) return;
    const l = el.offsetLeft - listEl.offsetLeft;
    if (l < listEl.scrollLeft) listEl.scrollLeft = l;
    else if (l + el.offsetWidth > listEl.scrollLeft + listEl.clientWidth) {
      listEl.scrollLeft = l + el.offsetWidth - listEl.clientWidth;
    }
  }
  onMount(() => {
    // The list's own box does not change size when a page is added past the
    // fit point (it just scrolls), so observe each page as well.
    const ro = new ResizeObserver(() => { measureOverflow(); revealActive(); });
    const watch = () => {
      ro.disconnect();
      if (!listEl) return;
      ro.observe(listEl);
      for (const c of Array.from(listEl.children)) ro.observe(c);
    };
    createEffect(on(() => pages().map((p) => p.id + p.name).join('|'), () => {
      queueMicrotask(() => { watch(); measureOverflow(); revealActive(); });
    }));
    onCleanup(() => ro.disconnect());
  });
  createEffect(on(activePageId, () => queueMicrotask(revealActive), { defer: true }));

  // ─── Page peek gesture (SD-8) ─────────────────────────────
  // Play + show only — in edit mode a pill press is a plain switch (no peek)
  // so authoring page-switches stay predictable. We DON'T capture the pointer
  // until the hold actually lands, so a pre-hold horizontal drag still scrolls
  // the pills row; once peeking, capture guarantees the release snaps back
  // even if the finger drifted off the pill.
  const [peekingId, setPeekingId] = createSignal<string | null>(null);
  let holdTimer: number | undefined;
  let returnPageId: string | null = null;
  let startX = 0;
  let startY = 0;
  let heldEl: HTMLButtonElement | null = null;
  let heldPointerId = -1;

  function resetGesture(): void {
    if (holdTimer !== undefined) { clearTimeout(holdTimer); holdTimer = undefined; }
    returnPageId = null;
    heldEl = null;
    heldPointerId = -1;
  }
  onCleanup(() => { if (holdTimer !== undefined) clearTimeout(holdTimer); });

  function onPillDown(ev: PointerEvent & { currentTarget: HTMLButtonElement }, pageId: string): void {
    if (isEditMode()) return; // edit: pointerup does a plain switch, no peek
    heldEl = ev.currentTarget;
    heldPointerId = ev.pointerId;
    returnPageId = activePageId();
    startX = ev.clientX;
    startY = ev.clientY;
    if (holdTimer !== undefined) clearTimeout(holdTimer);
    holdTimer = window.setTimeout(() => {
      holdTimer = undefined;
      // Held long enough → peek. No-op when holding the already-active pill.
      if (returnPageId !== null && pageId !== returnPageId) {
        heldEl?.setPointerCapture?.(heldPointerId);
        setPeekingId(pageId);
        switchPage(pageId);
      }
    }, PAGE_PEEK_HOLD_MS);
  }

  function onPillMove(ev: PointerEvent): void {
    if (peekingId() !== null) return;      // peeking: capture owns the gesture
    if (holdTimer === undefined) return;   // no pending hold to cancel
    const dx = ev.clientX - startX;
    const dy = ev.clientY - startY;
    if (dx * dx + dy * dy > PAGE_PEEK_MOVE_TOL * PAGE_PEEK_MOVE_TOL) {
      // Moved before the hold landed — a scroll/drag, not a press-to-peek.
      clearTimeout(holdTimer);
      holdTimer = undefined;
    }
  }

  function onPillUp(_ev: PointerEvent, pageId: string): void {
    const wasPeeking = peekingId() !== null;
    if (wasPeeking) {
      heldEl?.releasePointerCapture?.(heldPointerId);
      if (returnPageId !== null) switchPage(returnPageId); // snap back
      setPeekingId(null);
    } else {
      // Quick tap (or edit mode) — normal switch, stays.
      switchPage(pageId);
    }
    resetGesture();
  }

  function onPillCancel(): void {
    if (peekingId() !== null && returnPageId !== null) switchPage(returnPageId);
    setPeekingId(null);
    resetGesture();
  }

  return (
    <Show when={pages().length > 1}>
      <aside class="pages-pills" aria-label="Pages" data-overflow={overflowing() ? 'true' : 'false'}>
        <Show when={overflowing()}>
          <button
            ref={prevEl}
            type="button"
            class="pages-pills-arrow"
            onClick={() => go(-1)}
            disabled={activeIndex() <= 0}
            title="Previous page"
            aria-label="Previous page"
          >
            ‹
          </button>
        </Show>
        <div class="pages-pills-list" ref={listEl}>
          <For each={pages()}>
            {(p, i) => (
              <Show
                when={renamingId() !== p.id}
                fallback={
                  <span class="pages-pill" data-active={p.id === activePageId() ? 'true' : 'false'} data-hue={p.color} data-renaming="true">
                    <input
                      class="pages-pill-input"
                      value={p.name}
                      aria-label="Page name"
                      ref={(el) => queueMicrotask(() => { el.focus(); el.select(); })}
                      onPointerDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') { e.currentTarget.value = p.name; e.currentTarget.blur(); }
                      }}
                      onBlur={(e) => {
                        setRenamingId(null);
                        const v = e.currentTarget.value;
                        if (v.trim() && v !== p.name) renamePage(p.id, v);
                      }}
                    />
                  </span>
                }
              >
              <button
                type="button"
                class="pages-pill"
                data-active={p.id === activePageId() ? 'true' : 'false'}
                data-peeking={peekingId() === p.id ? 'true' : 'false'}
                // A running timeline just fired something on this page and
                // you are looking at another one (DEC-269).
                data-tl-glow={pageGlowing(p.id) ? 'true' : undefined}
                data-hue={p.color}
                data-compact={isDefaultName(p.name) ? 'true' : 'false'}
                onPointerDown={(e) => onPillDown(e, p.id)}
                onPointerMove={onPillMove}
                onPointerUp={(e) => onPillUp(e, p.id)}
                onPointerCancel={onPillCancel}
                // Keyboard activation only (Enter/Space synthesize a click with
                // detail 0); pointer taps are handled by onPointerUp above, so
                // ignore their trailing click to avoid a double / stale switch.
                onClick={(e) => { if (e.detail === 0) switchPage(p.id); }}
                onDblClick={() => setRenamingId(p.id)}
                title={`Switch to ${p.name} (hold to peek, double-click to rename)`}
              >
                {/* A still-default "Page N" carries no info beyond position,
                    so it collapses to a single numbered square; a renamed
                    page shows "N · Name" so the index still anchors it. */}
                <Show
                  when={!isDefaultName(p.name)}
                  fallback={<span class="pages-pill-num">{i() + 1}</span>}
                >
                  <span class="pages-pill-num">{i() + 1}</span>
                  <span class="pages-pill-sep">·</span>
                  <span class="pages-pill-name">{truncate(p.name, MAX_PILL_NAME_CHARS)}</span>
                </Show>
              </button>
              </Show>
            )}
          </For>
        </div>
        <Show when={overflowing()}>
          <button
            ref={nextEl}
            type="button"
            class="pages-pills-arrow"
            onClick={() => go(1)}
            disabled={activeIndex() === -1 || activeIndex() >= pages().length - 1}
            title="Next page"
            aria-label="Next page"
          >
            ›
          </button>
        </Show>
        <button
          type="button"
          class="pages-pills-manage"
          onClick={openPageManager}
          title="Manage pages (rename, reorder, duplicate, delete)"
          aria-label="Manage pages"
        >
          ⋯
        </button>
      </aside>
    </Show>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

/** A page whose name is still the auto-generated "Page N" says nothing its
 *  position doesn't already, so the pill shows just the number (no redundant
 *  "N · Page N"). Any custom name — even "Page A" — renders the full label. */
function isDefaultName(name: string): boolean {
  return /^Page \d+$/.test(name);
}

/** Map a PageHue token to the CSS custom property defined in app.css. The
 *  component sets `data-hue` and styles read `--page-hue-<token>`. Exposed
 *  here so the PageManager modal can use the same swatch values. */
export function hueVar(h: PageHue): string {
  return `var(--page-hue-${h})`;
}
